import React, { useCallback, useEffect, useState } from 'react';
import * as hazardGateway from '../db/hazardGateway';
import { drainOutbox } from '../services/outboxDrain';

type ListedOp = Awaited<ReturnType<typeof hazardGateway.listOutboxOps>>[number];

function labelFor(op: ListedOp): string {
  const text = typeof op.payload.description === 'string'
    ? op.payload.description
    : typeof op.payload.comment === 'string'
      ? op.payload.comment
      : op.entityId;
  return `${op.kind}: ${text.slice(0, 80)}`;
}

const OutboxStatus: React.FC = () => {
  const [ops, setOps] = useState<ListedOp[]>([]);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    setOps(await hazardGateway.listOutboxOps());
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const pending = ops.filter((op) => op.status === 'pending').length;
  const failed = ops.filter((op) => op.status === 'dead').length;
  if (ops.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-20 right-4 z-40">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="rounded-full bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow"
      >
        {failed > 0 ? `${failed} failed` : `${pending} pending`}
      </button>
      {open && (
        <ul className="mt-2 max-h-64 w-72 overflow-y-auto rounded-lg bg-white p-2 text-sm shadow dark:bg-deck-panel">
          {ops.map((op) => (
            <li key={op.id} className="border-b border-gray-200 py-2 last:border-b-0 dark:border-white/10">
              <p className="text-gray-800 dark:text-gray-100">{labelFor(op)}</p>
              <p className="text-xs text-gray-500">{op.status === 'dead' ? 'Failed' : 'Pending sync'}</p>
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  className="text-xs font-semibold text-blue-600"
                  onClick={() => {
                    void hazardGateway.retryOutboxOp(op.id).then(() => drainOutbox()).then(refresh);
                  }}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-red-600"
                  onClick={() => {
                    void hazardGateway.discardOutboxOp(op.id).then(refresh);
                  }}
                >
                  Discard
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default OutboxStatus;
